import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button, Section } from '@/components/ui'
import { FugueContactForm } from '@/components/FugueContactForm'
import { fugueLabProjects } from '@/data/portfolio'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import type { AudienceSegment } from '@/data/whoWeServe'

export function AudiencePageTemplate({ segment }: { segment: AudienceSegment }) {
  const url = `/who-we-help/${segment.slug}`
  const breadcrumbs = generateBreadcrumbs(url, breadcrumbLabels)

  useSEO({
    title: segment.seoTitle,
    description: segment.seoDescription,
    url,
    canonical: url,
    breadcrumbs,
    schema: createServiceSchema({
      name: segment.seoTitle,
      description: segment.seoDescription,
      url,
    }),
  })

  // Hero entrance only. The per-section staggered reveals went with the cards.
  const heroRef = useHeroReveal<HTMLElement>(0.2)

  const projects = fugueLabProjects
    .filter((project) => project.clientTypes?.some((type) => segment.portfolioTypes.includes(type)))
    .slice(0, 3)

  const linkFocus = 'focus-visible:outline-primary focus-visible:outline-2 focus-visible:outline-offset-4'

  return (
    <div className="mx-auto max-w-6xl px-5 pt-16 md:px-12">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 pb-20 text-center md:pb-28">
        <h1 className="text-fluid-h1">{segment.heroHeadline}</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">{segment.heroBody}</p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <m.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </m.div>
          </a>
        </div>
      </section>

      {/* Pain points: a two-column typographic list. The problem titles carry
          it in the display face; no boxes. */}
      <Section heading="Sound familiar?">
        <div className="grid gap-x-12 gap-y-12 md:grid-cols-2">
          {segment.painPoints.map((pain) => (
            <div key={pain.title} className="space-y-3">
              <h3 className="text-fluid-h4 leading-[1.15]">{pain.title}</h3>
              <p className="text-muted-foreground max-w-[52ch] leading-relaxed">{pain.description}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* What we build: the page's one change of ground, so the answer reads as
          a distinct movement from the problem above it. */}
      <Section tone="raised" bleed heading="What we build for you">
        <div className="grid gap-x-12 gap-y-10 md:grid-cols-2">
          {segment.whatWeBuild.map((item) => (
            <div key={item.title} className="space-y-2">
              <h3 className="font-display text-xl">{item.title}</h3>
              <p className="text-muted-foreground max-w-[52ch] leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Case studies */}
      {segment.caseStudies.length > 0 && (
        <Section size="compact" heading="From the case files">
          <ul className="space-y-4">
            {segment.caseStudies.map((caseStudy) => (
              <li key={caseStudy.href}>
                <Link
                  to={caseStudy.href}
                  className={`font-display hover:text-primary text-xl transition-colors duration-200 md:text-2xl ${linkFocus}`}
                >
                  {caseStudy.label}
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Portfolio proof: frameless, as on the home page. The screenshot is the
          proof; the shared bordered PortfolioCard would box it back up. */}
      {projects.length > 0 && (
        <Section heading="Sites we build and run">
          <div className="grid gap-x-10 gap-y-14 md:grid-cols-3">
            {projects.map((project) => (
              <Link
                key={project.slug}
                to={`/portfolio/${project.slug}`}
                className={`group block space-y-4 ${linkFocus}`}
              >
                <div className="bg-surface aspect-[4/3] overflow-hidden rounded-md">
                  <img
                    src={project.image}
                    alt=""
                    width={600}
                    height={450}
                    loading="lazy"
                    style={project.imagePosition ? { objectPosition: project.imagePosition } : undefined}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.02] motion-reduce:transition-none"
                  />
                </div>
                <h3 className="text-fluid-h4 group-hover:text-primary transition-colors duration-200">
                  {project.title}
                </h3>
              </Link>
            ))}
          </div>
          <Link
            to="/portfolio"
            className={`text-primary mt-12 inline-block font-medium underline decoration-1 underline-offset-4 hover:decoration-2 ${linkFocus}`}
          >
            See the full portfolio
          </Link>
        </Section>
      )}

      {/* FAQ */}
      <Section size="compact" heading="Questions that come up">
        <dl className="max-w-3xl space-y-10">
          {segment.faqs.map((faq) => (
            <div key={faq.question} className="space-y-2">
              <dt className="font-display text-xl">{faq.question}</dt>
              <dd className="text-muted-foreground leading-relaxed">{faq.answer}</dd>
            </div>
          ))}
        </dl>
      </Section>

      {/* CTA: set as type on a 5/7 split rather than a featured card. */}
      <Section>
        <div className="grid gap-6 md:grid-cols-12 md:gap-10">
          <h2 className="text-fluid-h3 md:col-span-5">{segment.ctaHeading}</h2>
          <div className="space-y-6 md:col-span-7">
            <p className="text-muted-foreground text-fluid-body max-w-[60ch] leading-relaxed">{segment.ctaBody}</p>
            <a
              href="/book"
              target="_blank"
              rel="noopener noreferrer"
              className={`text-primary inline-block font-medium underline decoration-1 underline-offset-4 hover:decoration-2 ${linkFocus}`}
            >
              Book a Free Call
            </a>
          </div>
        </div>
      </Section>

      <FugueContactForm />
    </div>
  )
}
